import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { t } from 'i18next';
import {
  Flyout, FlyoutItem, FlyoutTitle, Input, type FlyoutPlacement,
} from '@datanomik/core-ui';
import { ArrowLeftSLine, ArrowRightSLine, Search2Line } from '@datanomik/core-ui/icons';

import type { ShellNavChild, ShellNavEntry } from './types';
import { LockPill } from './NavPills';

interface NavSubmenuProps {
  entry: ShellNavEntry;
  anchor: HTMLElement | null;
  open: boolean;
  placement?: FlyoutPlacement;
  width?: number;
  onKeepOpen: () => void;
  onLeave: () => void;
  onClose: () => void;
}

const norm = (value: string) =>
  value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const hasCategories = (rows: ShellNavChild[]) => rows.some((row) => row.children?.length);

export const NavSubmenu: React.FC<NavSubmenuProps> = ({
  entry, anchor, open, placement = 'right', width, onKeepOpen, onLeave, onClose,
}) => {
  const [loaded, setLoaded] = useState<ShellNavChild[] | null>(null);
  const [query, setQuery] = useState('');
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !entry.loadChildren || loaded) return undefined;
    let cancelled = false;
    void entry.loadChildren().then((items) => {
      if (!cancelled) setLoaded(items);
    });
    return () => { cancelled = true; };
  }, [open, entry, loaded]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setOpenCategory(null);
    }
  }, [open]);

  const rows = useMemo(
    () => loaded ?? entry.children ?? [],
    [loaded, entry.children],
  );
  const drills = hasCategories(rows);
  const searching = query.trim().length > 0;

  const results = useMemo(() => {
    if (!searching) return [];
    const flat = drills ? rows.flatMap((category) => category.children ?? []) : rows;
    const term = norm(query.trim());
    return flat.filter((row) => norm(row.label).includes(term));
  }, [drills, rows, query, searching]);

  const category = drills && openCategory
    ? rows.find((row) => row.key === openCategory) ?? null
    : null;

  if (!open || rows.length === 0) return null;

  const drill = (key: string | null) => {
    onKeepOpen();
    setOpenCategory(key);
  };

  const handleLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!next || e.currentTarget.contains(next)) return;
    onLeave();
  };

  const leaf = (row: ShellNavChild, index: number) => (
    <FlyoutItem
      key={row.key}
      index={index}
      active={row.active}
      icon={row.icon}
      description={row.description}
      endAdornment={row.locked ? <LockPill /> : undefined}
      {...(row.to
        ? { as: Link, to: row.to, onClick: onClose }
        : {
          onClick: () => {
            row.onSelect?.();
            onClose();
          },
        })}
    >
      {row.label}
    </FlyoutItem>
  );

  const body = () => {
    if (searching) {
      if (results.length === 0) {
        return <p className="text-muted-foreground px-3 py-3 text-[12px]">{t('No results found')}</p>;
      }
      return results.map(leaf);
    }

    if (category) {
      return (
        <>
          <button
            type="button"
            data-slot="flyout-item"
            onClick={() => drill(null)}
            className="text-muted-foreground hover:text-foreground hover:bg-muted mb-0.5 flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold transition-colors"
          >
            <ArrowLeftSLine className="size-4 shrink-0" />
            <span className="text-foreground min-w-0 flex-1 truncate">{category.label}</span>
          </button>
          {(category.children ?? []).map(leaf)}
        </>
      );
    }

    if (drills) {
      return rows.map((row, index) => (row.children?.length ? (
        <FlyoutItem
          key={row.key}
          index={index}
          onClick={() => drill(row.key)}
          endAdornment={(
            <span className="flex items-center gap-1">
              <span className="text-muted-foreground text-[11px]">
                {row.count ?? row.children.length}
              </span>
              <ArrowRightSLine className="text-muted-foreground size-4" />
            </span>
          )}
        >
          {row.label}
        </FlyoutItem>
      ) : leaf(row, index)));
    }

    return rows.map((row, index) => (
      <React.Fragment key={row.key}>
        {row.group && row.group !== rows[index - 1]?.group ? (
          <FlyoutTitle className={index === 0 ? undefined : 'pt-3'}>{row.group}</FlyoutTitle>
        ) : null}
        {leaf(row, index)}
      </React.Fragment>
    ));
  };

  return (
    <Flyout
      anchor={anchor}
      open={open}
      placement={placement}
      width={width}
      onEscape={onClose}
      onPointerEnter={onKeepOpen}
      onPointerLeave={handleLeave}
      aria-label={entry.submenuTitle ?? entry.label}
    >
      {drills || !rows.some((row) => row.group) ? (
        <FlyoutTitle>{entry.submenuTitle ?? entry.label}</FlyoutTitle>
      ) : null}

      {drills && !category ? (
        <div className="px-1 pt-0.5 pb-1.5">
          <Input
            type="text"
            value={query}
            placeholder={t('Search')}
            aria-label={t('Search')}
            startAdornment={<Search2Line aria-hidden="true" />}
            onChange={(e) => setQuery(e.target.value)}
            // Escape still closes the panel; every other key is just typing.
            onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation(); }}
          />
        </div>
      ) : null}

      <div className="max-h-[min(60vh,420px)] overflow-y-auto">{body()}</div>
    </Flyout>
  );
};

export default NavSubmenu;
